import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

interface Props {
  settings: Record<string, unknown>;
  updateSetting: (key: string, value: unknown) => void;
}

export default function SettingsTexts({ settings, updateSetting }: Props) {
  const { t } = useTranslation("admin");
  return (
    <div className="space-y-6">
      <div>
        <h3 className="text-lg font-semibold mb-1">{t("settings.texts.title")}</h3>
        <p className="text-sm text-muted-foreground mb-4">{t("settings.texts.description")}</p>
      </div>

      <div className="space-y-4">
        <div className="space-y-2">
          <Label>{t("settings.texts.homeSubtitle")}</Label>
          <Input value={settings.home_subtitle || ""} onChange={(e) => updateSetting("home_subtitle", e.target.value)} placeholder={t("settings.texts.homeSubtitlePlaceholder")} />
          <p className="text-xs text-muted-foreground">{t("settings.texts.homeSubtitleHint")}</p>
        </div>
        <div className="space-y-2">
          <Label>{t("settings.texts.loginText")}</Label>
          <Input value={settings.login_text || ""} onChange={(e) => updateSetting("login_text", e.target.value)} placeholder={t("settings.texts.loginTextPlaceholder")} />
        </div>
        <div className="space-y-2">
          <Label>{t("settings.texts.welcomeText")}</Label>
          <Input value={settings.welcome_text || ""} onChange={(e) => updateSetting("welcome_text", e.target.value)} placeholder={t("settings.texts.welcomeTextPlaceholder")} />
        </div>
        <div className="space-y-2">
          <Label>{t("settings.texts.bookingConfirmation")}</Label>
          <Textarea
            value={settings.booking_confirmation_text || ""}
            onChange={(e) => updateSetting("booking_confirmation_text", e.target.value)}
            placeholder={t("settings.texts.bookingConfirmationPlaceholder")}
            rows={3}
          />
          <p className="text-xs text-muted-foreground">{t("settings.texts.bookingConfirmationHint")}</p>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">{t("settings.texts.sharingTitle")}</h4>
        <p className="text-xs text-muted-foreground mb-4">
          {t("settings.texts.sharingDescription")}
        </p>
        <div className="space-y-4">
          <div className="space-y-2">
            <Label>{t("settings.texts.shareHeadingOpen")}</Label>
            <Input
              value={settings.confirmation_share_text_open || ""}
              onChange={(e) => updateSetting("confirmation_share_text_open", e.target.value)}
              placeholder={t("settings.texts.shareHeadingOpenPlaceholder")}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("settings.texts.shareHeadingPrivate")}</Label>
            <Input
              value={settings.confirmation_share_text_private || ""}
              onChange={(e) => updateSetting("confirmation_share_text_private", e.target.value)}
              placeholder={t("settings.texts.shareHeadingPrivatePlaceholder")}
            />
          </div>
          <div className="space-y-2">
            <Label>{t("settings.texts.shareMessageOpen")}</Label>
            <Textarea
              value={settings.confirmation_share_message_open || ""}
              onChange={(e) => updateSetting("confirmation_share_message_open", e.target.value)}
              placeholder={t("settings.texts.shareMessageOpenPlaceholder")}
              rows={2}
            />
            <p className="text-xs text-muted-foreground">{t("settings.texts.variablesHint")}</p>
          </div>
          <div className="space-y-2">
            <Label>{t("settings.texts.shareMessagePrivate")}</Label>
            <Textarea
              value={settings.confirmation_share_message_private || ""}
              onChange={(e) => updateSetting("confirmation_share_message_private", e.target.value)}
              placeholder={t("settings.texts.shareMessagePrivatePlaceholder")}
              rows={2}
            />
            <p className="text-xs text-muted-foreground">{t("settings.texts.variablesHint")}</p>
          </div>
        </div>
      </div>

      <div className="border-t pt-4">
        <h4 className="font-medium mb-3">{t("settings.texts.legalTitle")}</h4>
        <div className="grid gap-4 sm:grid-cols-2">
          <div className="space-y-2">
            <Label>{t("settings.texts.termsUrl")}</Label>
            <Input value={settings.terms_url || ""} onChange={(e) => updateSetting("terms_url", e.target.value)} placeholder="https://..." />
          </div>
          <div className="space-y-2">
            <Label>{t("settings.texts.privacyUrl")}</Label>
            <Input value={settings.privacy_url || ""} onChange={(e) => updateSetting("privacy_url", e.target.value)} placeholder="https://..." />
          </div>
        </div>
      </div>
    </div>
  );
}
